import * as React from "react"
import { useShell } from "../shell/context"
import { drawIntro, type Nodes } from "./intro"
import { CARDS, INTRO_END, PERSPECTIVE, SLIDER_START, carouselPose, mobilePose, progress, put } from "./motion"
import AwardPanel, { preloadAward } from "./AwardOverlay"
import "./home.css"

// The home: a 3D carousel of projects that opens with the reel intro, then
// runs on its own (drag, wheel, arrow keys and clicks take over for a moment).
// The stage is designed at 1680x1080 (desktop) or 402 wide (phone) and scaled
// to the screen; --inverse undoes that scale for things that keep their size.

export type CardImage = { src?: string; srcSet?: string; alt?: string; positionX?: number; positionY?: number; tone?: string; mobile?: { src: string; srcSet?: string } }
export type HomeProps = {
    images: Record<number, CardImage | undefined> // slot 1..14; 1-4 also play in the intro
    videos?: Record<number, string | undefined> // muted, scrubbed by the carousel
    reelUrl?: string
    workUrl: string
    background: string
    reelColor: string
    autoSpeed: number // cards per minute
    startDelay: number // s after the intro
    motionBlur: number
    videoSpeed: number
}

const ASSETS = {
    arrow: "https://framerusercontent.com/images/rkwBqfDaAFY3a9hEuIRpXd2hz0.svg",
    award: "https://framerusercontent.com/images/4xP1q3SKiXP45NBZl4ouGe8uTtI.svg",
    awardArrow: "https://framerusercontent.com/images/DykaTWntbIE8LctnNSD95tITLi4.svg",
}
const HEADLINE = {
    desktop: ["Independent Designer of premium digital", "experiences, emerging interfaces and brands.", "former head of design at dept®."],
    phone: ["Independent Designer of", "premium digital experiences,", "emerging interfaces and brands.", "former head of design at dept®."],
}
const slot = (i: number) => (((i % 14) + 14) % 14) + 1 // card index -> image slot
const wrapOffset = (i: number, phase: number) => ((((i - phase + 7) % 14) + 14) % 14) - 7

/** Card video: muted, never "played"; the carousel sets its frame. */
function CardVideo({ src }: { src: string }) {
    return (
        <video
            // "#t=0.001" makes Safari paint the first frame
            src={src.includes("#") ? src : src + "#t=0.001"}
            muted
            playsInline
            autoPlay
            preload="auto"
            ref={(el) => {
                // iOS decodes no frame until the video has played once: start it
                // muted, pause straight away, then the carousel takes over
                if (!el || el.dataset.primed) return
                el.dataset.primed = "true"
                el.muted = el.defaultMuted = true
                el.setAttribute("muted", "")
                el.addEventListener("playing", () => el.pause(), { once: true })
                el.play()?.catch(() => {
                    try {
                        el.load() // autoplay blocked: still load, so seeking shows frames
                    } catch {}
                })
            }}
            onLoadedData={(e) => (e.currentTarget.dataset.ready = "true")}
            onLoadedMetadata={(e) => (e.currentTarget.dataset.ready = "true")}
            disablePictureInPicture
            aria-hidden="true"
            data-scrub="true"
            className="h-media"
        />
    )
}

export default function Home(p: HomeProps) {
    const shell = useShell()
    const settings = React.useRef(p)
    settings.current = p
    const root = React.useRef<HTMLElement>(null)
    const stage = React.useRef<HTMLDivElement>(null)
    const blurNode = React.useRef<SVGFEGaussianBlurElement>(null)
    const nodes = React.useRef<Nodes>({})
    const node = (key: string) => (el: HTMLElement | null) => {
        if (el) nodes.current[key] = el
    }
    const stageScale = React.useRef(1)
    // Phones: on screens shorter than the 874 design, the gap above the tile
    // shrinks first (by up to 104), then the tile's height (min 240).
    const [mobile, setMobile] = React.useState(false)
    const [mLayout, setMLayout] = React.useState({ top: 200, h: 432, H: 874 })
    const layout = React.useRef({ mobile: false, ...mLayout })
    const poseFor = (o: number) => (layout.current.mobile ? mobilePose(o, layout.current.h) : carouselPose(o))
    const intro = () => ({ ...layout.current, pose: poseFor })
    const slider = React.useRef({ target: 0, value: 0, velocity: 0, ready: false, dragging: false, travel: 0, lastX: 0, lastTime: 0, resumeAt: 0, snapAt: 0 })
    const [reelOpen, setReelOpen] = React.useState(false)
    // the award's panel (its tab on its edge)
    const [awardOpen, setAwardOpen] = React.useState(false)

    // Fit the stage to the screen.
    React.useEffect(() => {
        const size = () => {
            const el = root.current
            if (!el) return
            const r = el.getBoundingClientRect()
            const isMobile = r.width > 0 && r.width < 768
            layout.current.mobile = isMobile
            setMobile(isMobile)
            const W = isMobile ? 402 : 1680
            let H = 1080
            if (isMobile) {
                H = r.height / (r.width / W) || 874
                const d = Math.max(0, 874 - H)
                // (the group sits 24px lower than the design, 200 vs 176, so the
                // intro's rise from screen centre is shorter)
                const next = { top: 200 - Math.min(d, 104), h: Math.max(240, 432 - Math.max(0, d - 104)), H }
                const prev = layout.current
                if (Math.abs(prev.H - next.H) > 0.5 || prev.top !== next.top || prev.h !== next.h) {
                    Object.assign(layout.current, next)
                    setMLayout(next)
                }
            }
            // phones scale by width (the centre tile keeps its 16px side margins);
            // desktop fits both axes
            const scale = isMobile ? r.width / W : Math.min(r.width / W, r.height / H)
            stageScale.current = scale || 1
            const s = stage.current!.style
            s.transform = `translate(-50%,-50%) scale(${scale})`
            s.setProperty("--inverse", String(1 / scale))
            s.setProperty("--edgeX", `${(r.width / scale - W) / 2}px`)
            s.setProperty("--edgeY", `${(r.height / scale - H) / 2}px`)
        }
        size()
        const ro = new ResizeObserver(size)
        ro.observe(root.current!)
        return () => ro.disconnect()
    }, [])

    // Intro, then the carousel.
    React.useEffect(() => {
        // the shell's logo, local time and menu button are part of the intro
        // (mounted by now: effects run after every ref is attached)
        if (shell) {
            Object.assign(nodes.current, shell.intro.current)
            ;(shell as any).prepareHomeIntro?.()
        }
        let frame = 0,
            disposed = false,
            visible = true
        const st = slider.current
        Object.assign(st, { target: 0, value: 0, velocity: 0, ready: false, dragging: false, resumeAt: 0, snapAt: 0 })
        drawIntro(nodes.current, 0, intro())

        const paint = (phase: number) => {
            const n = nodes.current
            for (let i = -6; i <= 7; i++) {
                const el = n["slide" + i]
                if (!el) continue
                const offset = wrapOffset(i, phase),
                    a = Math.abs(offset),
                    ps = poseFor(offset)
                put(el, "width", `${ps.w}px`)
                put(el, "height", `${ps.h}px`)
                put(el, "transform", `translate(-50%,-50%) translateX(${ps.x}px) translateZ(${ps.z}px) rotateY(${ps.angle}deg)`)
                put(el, "borderRadius", `${ps.r}px`)
                put(el, "zIndex", String(1000 - Math.round(a * 10)))
                put(el, "opacity", String(Math.max(0, Math.min(1, 6 - a))))
                put(el, "pointerEvents", a < 6 ? "auto" : "none")
                // card videos follow the carousel: real time at the auto speed,
                // faster/backwards when it's moved faster/backwards
                if (a < 2.5) {
                    const vid = el.querySelector("video[data-scrub]") as HTMLVideoElement | null
                    if (vid && vid.dataset.ready && vid.duration > 0 && !vid.seeking) {
                        const perCard = (60 / Math.max(0.5, settings.current.autoSpeed || 6)) * Math.max(0.05, settings.current.videoSpeed)
                        const d = vid.duration,
                            time = (((phase * perCard) % d) + d) % d
                        if (Math.abs(vid.currentTime - time) > 1 / 60) vid.currentTime = time
                    }
                }
            }
        }
        // Horizontal motion blur from the (smoothed) speed: streak width =
        // motionBlur x 0.05 x speed x screen width, as a Gaussian (sigma = 0.29 x width).
        let blurSpeed = 0,
            lastBlur = ""
        const motionBlur = (dt: number) => {
            blurSpeed += (Math.min(Math.abs(st.velocity) * 0.12, 1.5) - blurSpeed) * (1 - Math.exp(-dt * 12))
            const width = root.current?.clientWidth || 1680
            const sigma = (Math.max(0, settings.current.motionBlur) * 0.05 * blurSpeed * width * 0.29) / stageScale.current
            const on = sigma > 0.3
            const value = on ? `${sigma.toFixed(2)} 0` : "0 0"
            if (value !== lastBlur && blurNode.current) {
                blurNode.current.setAttribute("stdDeviation", value)
                lastBlur = value
            }
            for (let i = -6; i <= 7; i++) put(nodes.current["slide" + i], "filter", on ? "url(#n27-motion-blur)" : "none")
        }

        let origin = 0,
            last = 0
        const tick = (now: number) => {
            if (disposed) return
            if (!visible) {
                last = now
                return
            }
            const dt = Math.min((now - last) / 1000 || 0.016, 1 / 30)
            last = now
            const t = now - origin
            if (t <= INTRO_END) drawIntro(nodes.current, t, intro(), t >= SLIDER_START)
            if (t >= SLIDER_START) {
                if (!st.ready) drawIntro(nodes.current, SLIDER_START, intro())
                st.ready = true
                if (st.snapAt && now >= st.snapAt) {
                    st.target = Math.round(st.target)
                    st.snapAt = 0
                }
                // auto-advance (eased in after the intro), then a spring to the target
                const { autoSpeed, startDelay } = settings.current
                if (!st.dragging && now >= st.resumeAt)
                    st.target += ((dt * Math.max(0, autoSpeed)) / 60) * progress(t, SLIDER_START + Math.max(0, startDelay) * 1000, 450, "linear")
                st.velocity += (180 * (st.target - st.value) - 30 * st.velocity) * dt
                st.value += st.velocity * dt
                paint(st.value)
                if (!layout.current.mobile) motionBlur(dt) // too heavy on phones
            }
            frame = requestAnimationFrame(tick)
        }
        const run = () => {
            if (disposed) return
            origin = last = performance.now()
            frame = requestAnimationFrame(tick)
        }
        const resume = () => {
            if (visible && !disposed) {
                cancelAnimationFrame(frame)
                last = performance.now()
                frame = requestAnimationFrame(tick)
            }
        }
        const io = new IntersectionObserver((entries) => {
            visible = entries[0].isIntersecting && document.visibilityState === "visible"
            if (origin) resume()
        })
        io.observe(root.current!)
        const visibility = () => {
            visible = document.visibilityState === "visible"
            if (origin) resume()
        }
        document.addEventListener("visibilitychange", visibility)
        // start once the type is in (the intro is timed around it)
        Promise.all([document.fonts.load('700 24px "NTO27Blubird"'), document.fonts.load('600 14px "NTO27Blubird"')]).then(run, run)
        return () => {
            disposed = true
            cancelAnimationFrame(frame)
            io.disconnect()
            document.removeEventListener("visibilitychange", visibility)
        }
    }, [])

    // ----- input: wheel, drag, keys, click a card -----
    const pause = (st: typeof slider.current) => (st.resumeAt = performance.now() + 1800)
    const wheel = (e: React.WheelEvent) => {
        const st = slider.current
        if (!st.ready) return
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 800 : 1
        st.target += (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY * 0.8) * unit * 0.004
        pause(st)
        st.snapAt = performance.now() + 150
    }
    const pointerDown = (e: React.PointerEvent) => {
        const st = slider.current
        if (!st.ready || e.button !== 0) return
        Object.assign(st, { dragging: true, travel: 0, lastX: e.clientX, lastTime: performance.now(), snapAt: 0 })
        e.currentTarget.setPointerCapture(e.pointerId)
    }
    const pointerMove = (e: React.PointerEvent) => {
        const st = slider.current
        if (!st.dragging) return
        const now = performance.now()
        const delta = e.clientX - st.lastX
        st.travel += Math.abs(delta)
        st.target -= delta * 0.005
        st.velocity = (-delta / Math.max(1, now - st.lastTime)) * 5
        st.lastX = e.clientX
        st.lastTime = now
    }
    const pointerUp = () => {
        const st = slider.current
        if (!st.dragging) return
        st.dragging = false
        st.target = Math.round(st.target + st.velocity * 0.3)
        pause(st)
    }
    const keyDown = (e: React.KeyboardEvent) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return
        e.preventDefault()
        const st = slider.current
        if (!st.ready) return
        st.target = Math.round(st.target) + (e.key === "ArrowRight" ? 1 : -1)
        pause(st)
    }
    React.useEffect(() => {
        const close = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return
            shell?.setMenuOpen(false)
            setReelOpen(false)
        }
        addEventListener("keydown", close)
        return () => removeEventListener("keydown", close)
    }, [])

    const img = p.images
    const hidden: React.CSSProperties = { opacity: 0 }
    const L = mLayout
    const imageStyle = (im?: CardImage): React.CSSProperties => ({
        objectPosition: `${(im?.positionX ?? 0.5) * 100}% ${(im?.positionY ?? 0.5) * 100}%`,
    })
    return (
        <div className="home" data-mobile={mobile ? "true" : "false"}>
            <main ref={root} onWheel={wheel} className="nto27 home-main" data-mobile={mobile ? "true" : "false"} style={{ background: p.background }}>
                <div ref={stage} className="h-stage" style={{ width: mobile ? 402 : 1680, height: mobile ? L.H : 1080 }}>
                    <div
                        role="region"
                        aria-roledescription="carousel"
                        aria-label="Browse projects"
                        tabIndex={0}
                        onPointerDown={pointerDown}
                        onPointerMove={pointerMove}
                        onPointerUp={pointerUp}
                        onPointerCancel={pointerUp}
                        onKeyDown={keyDown}
                        className="h-carousel"
                        style={{
                            top: mobile ? L.top : 280,
                            width: mobile ? 402 : 1680,
                            height: mobile ? L.h : 490,
                            perspective: PERSPECTIVE,
                            perspectiveOrigin: mobile ? `201px ${L.h / 2}px` : "840px 253px",
                        }}
                    >
                        <div className="h-track" style={{ left: mobile ? 201 : 840, top: mobile ? L.h / 2 : 253 }}>
                            {Array.from({ length: 14 }, (_, j) => j - 6)
                                .sort((a, b) => Math.abs(b) - Math.abs(a))
                                .map((i) => {
                                    const c = CARDS[Math.abs(i) - 1],
                                        side = Math.sign(i),
                                        center = i === 0,
                                        pose = mobile ? mobilePose(i, L.h) : carouselPose(i),
                                        // (on phones, its version reframed for the squarer tile, if it has one)
                                        image = mobile && img[slot(i)]?.mobile ? { ...img[slot(i)], ...img[slot(i)]!.mobile } : img[slot(i)],
                                        video = p.videos?.[slot(i)]
                                    // tiles with media get a neutral grey backing (no coloured
                                    // fringe at the edges); empty tiles keep their colours
                                    const media = center ? [1, 2, 3, 4].some((k) => img[k]?.src) || p.videos?.[1] : image?.src || video
                                    return (
                                        <div
                                            key={i}
                                            data-card-index={i}
                                            ref={(el) => {
                                                if (!el) return
                                                nodes.current["slide" + i] = el
                                                if (center) nodes.current.reel = el
                                                else if (c) nodes.current[`card${side}_${Math.abs(i) - 1}`] = el
                                            }}
                                            onClick={() => {
                                                const st = slider.current
                                                if (!st.ready || st.travel > 4) return
                                                st.target = Math.round(st.value + wrapOffset(i, st.value))
                                                pause(st)
                                            }}
                                            aria-hidden="true"
                                            className={"h-card" + (media ? " h-card-media" : "")}
                                            style={{
                                                width: pose.w,
                                                height: pose.h,
                                                opacity: center ? 1 : 0,
                                                // (a tile with an image: nothing behind it, unless it's slow to come: then its main colour)
                                                background: media ? undefined : center ? "#d9cbb2" : c?.color || ["#ffa500", "#3100ff", "#ff0096", "#00ee9b", "#ffffff"][(Math.abs(i) - 1) % 5],
                                                ["--tone" as any]: (center ? img[1]?.tone : image?.tone) || "#d6d6d6",
                                                transform: center
                                                    ? "translate(-50%,-50%) scale(0)"
                                                    : `translate(-50%,-50%) translateX(${pose.x}px) translateZ(${pose.z}px) rotateY(${pose.angle}deg)`,
                                            }}
                                        >
                                            {/* (decoded with the paint, never later: a tile changes size every frame
                                                as it moves, and Safari decoding it apart from the paint drew it
                                                blank on every other frame: the entering tile flickered) */}
                                            {!center && image?.src && (
                                                <img
                                                    src={image.src}
                                                    srcSet={image.srcSet}
                                                    sizes="40vw"
                                                    decoding="sync"
                                                    alt={image.alt || ""}
                                                    draggable={false}
                                                    className="h-media h-fade"
                                                    style={imageStyle(image)}
                                                    ref={(el) => el && el.complete && el.naturalWidth && el.setAttribute("data-in", "")}
                                                    onLoad={(e) => e.currentTarget.setAttribute("data-in", "")}
                                                />
                                            )}
                                            {!center && video && <CardVideo src={video} />}
                                            {/* first intro frame: image 4, under the three wipes */}
                                            {center && img[4]?.src && (
                                                <img src={img[4].src} srcSet={img[4].srcSet} sizes="50vw" decoding="sync" alt={img[4].alt || ""} draggable={false} className="h-media" style={imageStyle(img[4])} />
                                            )}
                                            {center &&
                                                ["#292929", "#1939df", p.reelColor].map((color, j) => {
                                                    // wipes show images 3, 2, then 1, which stays as the centre tile
                                                    const im = img[3 - j]
                                                    return (
                                                        <div
                                                            key={j}
                                                            ref={node("wipe" + j)}
                                                            className="h-wipe"
                                                            // a wipe with a photo has no fill colour, so nothing flashes before it paints
                                                            style={{ background: im?.src ? "transparent" : color }}
                                                        >
                                                            {im?.src && (
                                                                <img src={im.src} srcSet={im.srcSet} sizes="50vw" decoding="sync" alt={im.alt || ""} draggable={false} className="h-media" style={imageStyle(im)} />
                                                            )}
                                                            {j === 2 && p.videos?.[1] && <CardVideo src={p.videos[1]} />}
                                                        </div>
                                                    )
                                                })}
                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                    <div ref={node("dots")} aria-hidden="true" className="h-dots" style={{ left: mobile ? 201 : 840, top: mobile ? L.H / 2 : 533 }}>
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} ref={node("dot" + i)} className="h-dot" />
                        ))}
                    </div>
                    <div
                        ref={node("button")}
                        className="h-reel"
                        style={{
                            left: mobile ? 201 : 840.5,
                            top: mobile ? L.top + L.h / 2 : 539.5,
                            transform: mobile ? "translate(-50%,-50%) scale(calc(1.27 * var(--inverse, 1)))" : "translate(-50%,-50%) scale(var(--inverse, 1))",
                            ...hidden,
                        }}
                    >
                        <button className="reel-button" aria-label="Watch reel" onClick={() => p.reelUrl && setReelOpen(true)}>
                            <span ref={node("buttonSurface")} aria-hidden="true" className="h-reel-surface" />
                            <span ref={node("buttonText")} className="h-reel-label" style={hidden}>
                                Watch reel
                            </span>
                            <img ref={node("buttonArrow")} src={ASSETS.arrow} alt="" width={10} height={15} className="h-reel-arrow" style={hidden} />
                        </button>
                    </div>
                    <h1
                        className="h-headline"
                        style={
                            mobile
                                ? { left: "calc(16px * var(--inverse, 1) - var(--edgeX, 0px))", top: L.top + L.h + 16, width: 370, textAlign: "left", fontSize: 20.5, lineHeight: "25.5px" }
                                : { left: 538, top: 775, width: 604, textAlign: "center", fontSize: 23.172, lineHeight: "24.8px" }
                        }
                    >
                        {(mobile ? HEADLINE.phone : HEADLINE.desktop).map((line, i) => (
                            <span key={line} style={{ display: "block" }}>
                                <span ref={node("line" + i)} style={{ display: "block", ...hidden, whiteSpace: "nowrap" }}>
                                    {line}
                                </span>
                            </span>
                        ))}
                    </h1>
                    {/* ("Scroll to imagine" taken out for now) */}
                </div>
                <svg aria-hidden="true" width="0" height="0" className="h-defs">
                    <filter id="n27-motion-blur" x="-25%" y="0%" width="150%" height="100%" colorInterpolationFilters="sRGB">
                        <feGaussianBlur ref={blurNode} stdDeviation="0 0" />
                    </filter>
                </svg>
                {!mobile && (
                    <AwardPanel
                        open={awardOpen}
                        onToggle={setAwardOpen}
                        tabRef={node("award")}
                        tabStyle={hidden}
                        arrow={ASSETS.awardArrow}
                    />
                )}
                {reelOpen && p.reelUrl && (
                    <div role="dialog" aria-modal="true" aria-label="Showreel" className="h-reel-dialog">
                        <button autoFocus onClick={() => setReelOpen(false)} aria-label="Close reel" className="h-reel-close">
                            Close
                        </button>
                        <video src={p.reelUrl} controls autoPlay playsInline />
                    </div>
                )}
            </main>
        </div>
    )
}
